import React from 'react';
import { AlertTriangle, DollarSign, Phone, Target, Percent, TrendingUp } from 'lucide-react';

const CODE = 'GC-HVAC-NOCOOL-9696';

const PLACEMENTS = [
  {
    label: 'Meta UTM Link',
    where: "When you set up your Facebook/Instagram ad, paste this link into the 'Website URL' field in Ads Manager — instead of your plain website link.",
    why: 'This is what tells us a call or booking came from THIS specific ad — so we can tell you whether it\'s working.',
    example: `https://yourcompany.com/book?utm_source=facebook&utm_medium=paid_social&utm_campaign=${CODE}`,
  },
  {
    label: 'Google UTM Link',
    where: "When you build your Google Ads campaign, paste this link into the 'Final URL' field on the ad.",
    why: "Same idea — it marks every click from this Google ad, so we can tell you if Google is earning its budget.",
    example: `https://yourcompany.com/book?utm_source=google&utm_medium=paid_search&utm_campaign=${CODE}`,
  },
  {
    label: 'GBP UTM Link',
    where: 'Paste this link when you create a post or add a button on your Google Business Profile.',
    why: "Profile clicks are free leads — this makes sure they get counted for this campaign instead of disappearing.",
    example: `https://yourcompany.com/book?utm_source=google&utm_medium=organic_gbp&utm_campaign=${CODE}`,
  },
  {
    label: 'Email / SMS UTM Links',
    where: 'Use these links any time you share your booking page in an email or text message tied to this campaign.',
    why: 'Your customer list is your cheapest lead source — these links prove which campaign email actually booked jobs.',
    example: `https://yourcompany.com/book?utm_source=email&utm_medium=customer_list&utm_campaign=${CODE}`,
  },
  {
    label: 'CRM Source Name / Booking Note',
    where: "When a customer calls or books, type this note into your booking system (ServiceTitan, Jobber, Housecall Pro…) — or your notebook if you don't have one.",
    why: 'When your booking system sends us the job, we scan it for this code and count the finished job — and its revenue — toward this campaign.',
    example: `Booking source: ${CODE}`,
  },
  {
    label: 'Call Tracking Source / Tracking Phone',
    where: 'If you set a tracking phone number in My Brand, use THAT number in this specific ad instead of your main business line.',
    why: "Calls to that number can only have come from this ad — the cleanest proof there is that an ad made the phone ring.",
    example: `Ad shows (555) 999-0000 → main line stays (555) 111-2222`,
  },
  {
    label: 'Landing Page URL',
    where: 'This is the page all the tracking links above actually send people to — your booking page or website (set in My Brand).',
    why: "It should match the ad's offer. If the ad says '$50 off AC repair' and the page says nothing about it, people leave and your cost per lead climbs.",
    example: `https://yourcompany.com/book`,
  },
];

const NUMBERS = [
  {
    icon: DollarSign, label: 'Ad Spend',
    meaning: 'What you actually paid the platforms.',
    action: 'Enter it on the Campaign Performance card — every other number below depends on it being real.',
  },
  {
    icon: Phone, label: 'Cost per Lead / Call',
    meaning: 'Spend ÷ leads. For most home service ads, roughly $25–$50 per call is normal.',
    action: 'In range → leave the ad alone. Way above for 3+ days → change ONE thing (photo or headline), then wait again.',
  },
  {
    icon: Target, label: 'Cost per Booked Job',
    meaning: 'Spend ÷ jobs actually booked. The number agencies never show you.',
    action: "Compare it to your average job value. Paying $80 to book a $450 repair is a great trade. Paying $80 to book an $89 tune-up is not — unless those tune-ups turn into replacements.",
  },
  {
    icon: Percent, label: 'Close Rate',
    meaning: 'Booked jobs ÷ leads. How much of what the ads bring in you actually capture.',
    action: "Below ~30%? The problem usually isn't the ad — it's missed calls, slow callbacks, or the booking conversation. Fix that before spending more.",
  },
  {
    icon: TrendingUp, label: 'ROI / ROAS',
    meaning: 'Revenue from the campaign vs. what it cost. The only number that decides if marketing made you money.',
    action: 'Positive and steady → scale carefully (raise budget ~20%, wait a week). Negative after real volume → change the offer or channel, not just the budget.',
  },
];

export const TrackingCodeGuide = () => (
  <div className="space-y-10">
    {/* LESSON 4: where the code goes */}
    <div data-testid="tracking-code-guide">
      <h2 className="text-2xl font-bold text-white mb-2">Where your tracking code actually goes</h2>
      <p className="text-light-gray text-sm mb-2 leading-relaxed">
        Every campaign you start tracking gets its own code — for example <code className="text-lime bg-black px-2 py-0.5 rounded">{CODE}</code>.
        Here's exactly where each piece of your Tracking Package gets pasted, and why it matters.
      </p>
      <p className="text-xs text-gray mb-5">
        This is exactly how the system works under the hood: your links carry the code, your booking notes carry the code, and GritClicks scans incoming clicks and bookings for it. No code found = the lead can't be credited. We never guess.
      </p>
      <div className="space-y-3">
        {PLACEMENTS.map((p, i) => (
          <div key={p.label} className="bg-card-bg border border-divider rounded-xl p-5" data-testid={`tracking-guide-item-${i}`}>
            <h3 className="text-white font-bold mb-1.5">{i + 1}. {p.label}</h3>
            <p className="text-sm text-light-gray mb-1"><span className="text-lime font-semibold">Where it goes:</span> {p.where}</p>
            <p className="text-sm text-light-gray mb-2.5"><span className="text-orange font-semibold">Why it matters:</span> {p.why}</p>
            <code className="block text-xs text-lime bg-black border border-divider rounded-lg px-3 py-2 break-all">{p.example}</code>
          </div>
        ))}
      </div>
      <div className="mt-4 p-4 rounded-xl bg-black border border-orange/40" data-testid="tracking-forget-note">
        <p className="text-orange font-bold text-sm mb-1 flex items-center gap-2"><AlertTriangle size={15} /> What happens if I forget to use these?</p>
        <p className="text-sm text-light-gray leading-relaxed">
          Nothing breaks — but that lead or booking shows up as <span className="text-white font-semibold">"Unattributed"</span> instead of being counted toward this campaign's results.
          The jobs still happen and the money is still real; you just lose the ability to see which campaign earned it.
          That's not a broken system — it's an honest gap in tracking, and it's why the copy buttons exist: paste, don't retype.
        </p>
      </div>
    </div>

    {/* LESSON 5: reading your numbers */}
    <div data-testid="reading-numbers-section">
      <h2 className="text-2xl font-bold text-white mb-2">How to read your own numbers</h2>
      <p className="text-light-gray text-sm mb-5 leading-relaxed">
        Five numbers run your whole marketing operation. Here's what each one means — and what to actually do when it's good or bad.
      </p>
      <div className="space-y-3">
        {NUMBERS.map((n) => (
          <div key={n.label} className="bg-card-bg border border-divider rounded-xl p-5 flex gap-4">
            <n.icon size={20} className="text-lime shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-bold">{n.label}</h3>
              <p className="text-sm text-light-gray mt-0.5">{n.meaning}</p>
              <p className="text-sm text-gray mt-1"><span className="text-orange font-semibold">What to do:</span> {n.action}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-sm text-light-gray mt-4 bg-card-bg border border-divider rounded-lg p-4">
        💡 <span className="font-semibold text-white">You don't have to do this math yourself</span> — every campaign's KPI card calculates it live, and the Performance Check reads these numbers and tells you what to do next in plain English. But now you know <span className="text-lime font-semibold">why</span> it says what it says — which is the difference between owning a tool and owning a skill.
      </p>
    </div>
  </div>
);
